import { useState, useEffect } from "react";
import { X, Download, FileArchive, MapPinned, Loader2, FileBox, History } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MODULE_ORDER, MODULES } from "@/lib/modules";
import { useI18n } from "@/context/I18nContext";

export default function ExportDialog({ onClose }) {
  const { t } = useI18n();
  const [format, setFormat] = useState(() => localStorage.getItem("exp_format") || "csv");
  const [selected, setSelected] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("exp_mods")); if (Array.isArray(s) && s.length) return new Set(s); } catch { /* ignore */ }
    return new Set(MODULE_ORDER);
  });
  const [busy, setBusy] = useState(false);
  const [area, setArea] = useState(() => localStorage.getItem("exp_area") || "");
  const [areas, setAreas] = useState([]);
  const [asBuilt, setAsBuilt] = useState(null); // null | {done, total}
  const [history, setHistory] = useState([]);
  const [cov, setCov] = useState(null);
  const DXF_MODS = ["poles", "wells", "closures", "splitters", "olt", "odf", "cabinets", "cable_slack", "cables", "ducts", "microducts", "pipelines", "routes", "zones"];
  const [showDxf, setShowDxf] = useState(false);
  const [dxfEpsg, setDxfEpsg] = useState(() => { const v = Number(localStorage.getItem("dxf_epsg")); return v || 2180; });
  const [dxfMods, setDxfMods] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem("dxf_mods")); if (Array.isArray(s) && s.length) return new Set(s); } catch { /* ignore */ }
    return new Set(DXF_MODS);
  });

  const loadHistory = () => api.get("/export/as-built/history").then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { api.get("/export/areas").then((r) => setAreas(r.data || [])).catch(() => {}); loadHistory(); }, []);
  useEffect(() => {
    try { localStorage.setItem("exp_mods", JSON.stringify([...selected])); localStorage.setItem("exp_area", area); localStorage.setItem("exp_format", format); } catch { /* ignore */ }
  }, [selected, area, format]);

  const downloadHistory = async (id) => {
    try {
      const res = await api.get(`/export/as-built/download/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "komplet_powykonawczy.zip";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error(t("Nie udało się pobrać archiwum")); }
  };

  const toggle = (k) => setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allOn = selected.size === MODULE_ORDER.length;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(MODULE_ORDER));

  // KML holds only geometry — auto-deselect modules without geo so no empty folders are produced
  useEffect(() => {
    if (format === "kml") setSelected((s) => new Set([...s].filter((k) => MODULES[k].geo !== "none")));
  }, [format]);

  const doPassport = async () => {
    setBusy(true);
    try {
      const res = await api.get("/export/network-passport", { params: area.trim() ? { area: area.trim() } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "paszport_sieci.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("Paszport sieci gotowy — pobrano ZIP (CSV + KML + PDF)"));
    } catch { toast.error(t("Błąd eksportu paszportu sieci")); }
    finally { setBusy(false); }
  };

  const doAsBuilt = async () => {
    setBusy(true); setAsBuilt({ done: 0, total: 0 });
    try {
      const { data } = await api.post("/export/as-built/start");
      const jobId = data.job_id;
      // Odpytuj postęp aż do zakończenia
      const poll = async () => {
        const r = await api.get(`/export/as-built/status/${jobId}`);
        setAsBuilt({ done: r.data.done || 0, total: r.data.total || 0 });
        if (r.data.status === "done") return true;
        if (r.data.status === "error") throw new Error(r.data.error || "error");
        return false;
      };
      let done = false;
      while (!done) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, 800));
        // eslint-disable-next-line no-await-in-loop
        done = await poll();
      }
      const res = await api.get(`/export/as-built/download/${jobId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "komplet_powykonawczy.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("Komplet powykonawczy gotowy — pobrano ZIP (rozpływ muf + metryki łączy)"));
    } catch { toast.error(t("Błąd eksportu kompletu powykonawczego")); }
    finally { setBusy(false); setAsBuilt(null); loadHistory(); }
  };

  const doShp = async (tg) => {
    try {
      const res = await api.get(`/export/shapefile?target=${tg}${area.trim() ? `&area=${encodeURIComponent(area.trim())}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${tg}.shp.zip`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Pobrano Shapefile (ZIP)"));
    } catch { toast.error(t("Błąd eksportu Shapefile")); }
  };

  const doAll = async () => {
    try {
      const res = await api.get("/export/shapefile-all", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "fiberpass-gis-shapefile.zip";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Pobrano komplet Shapefile (ZIP)"));
    } catch { toast.error(t("Błąd eksportu")); }
  };

  const doCablesXlsx = async () => {
    try {
      const res = await api.get("/export/cables-xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "kable_optyczne.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Pobrano arkusz XLSX kabli"));
    } catch { toast.error(t("Błąd eksportu XLSX")); }
  };

  const doDxf = () => setShowDxf(true);
  const doDxfDownload = async () => {
    const mods = [...dxfMods];
    if (!mods.length) { toast.error(t("Zaznacz co najmniej jeden moduł")); return; }
    try {
      localStorage.setItem("dxf_epsg", String(dxfEpsg));
      localStorage.setItem("dxf_mods", JSON.stringify(mods));
    } catch { /* ignore */ }
    try {
      const res = await api.get(`/export/dxf?epsg=${dxfEpsg}&modules=${mods.join(",")}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "fiberpass-siec.dxf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(t("Pobrano plik DXF"));
      setShowDxf(false);
    } catch { toast.error(t("Błąd eksportu DXF")); }
  };

  const doCov = async () => {
    try { const { data } = await api.get("/export/zones-coverage"); setCov(data); }
    catch { toast.error(t("Nie udało się policzyć pokrycia")); }
  };

  const doExport = async () => {
    const modules = [...selected];
    if (!modules.length) { toast.error(t("Zaznacz co najmniej jeden moduł")); return; }
    setBusy(true);
    try {
      const res = await api.post("/export", { format, modules }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = format === "kml" ? "fiberpass_export.kml" : "fiberpass_csv.zip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Eksport gotowy — plik pobrany");
    } catch { toast.error("Błąd eksportu"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4" data-testid="export-dialog">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><Download size={18} className="text-cyan-400" /> {t("Eksport danych")}</h3>
          <button onClick={onClose} data-testid="export-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
        </div>
        <div className="overflow-y-auto p-5 space-y-4">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">{t("Format")}</div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setFormat("csv")} data-testid="export-format-csv"
                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm ${format === "csv" ? "border-cyan-500 bg-cyan-500/10 text-cyan-200" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
                <FileArchive size={18} /> {t("CSV (ZIP — plik na moduł)")}
              </button>
              <button onClick={() => setFormat("kml")} data-testid="export-format-kml"
                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm ${format === "kml" ? "border-cyan-500 bg-cyan-500/10 text-cyan-200" : "border-slate-700 text-slate-300 hover:border-slate-500"}`}>
                <MapPinned size={18} /> {t("KML (mapa — Google Earth)")}
              </button>
            </div>
            {format === "kml" && <p className="text-[11px] text-amber-300/80 mt-2">{t("KML zawiera tylko obiekty z lokalizacją (punkty i linie). Moduły bez geometrii (np. katalogi, monterzy) będą puste.")}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-slate-400 uppercase tracking-wide">{t("Co eksportować")} ({selected.size})</div>
              <div className="flex items-center gap-3">
                <button onClick={() => { setSelected(new Set(MODULE_ORDER)); setArea(""); }} data-testid="export-reset" className="text-xs text-slate-400 hover:text-slate-200">
                  {t("Przywróć domyślne")}
                </button>
                <button onClick={toggleAll} data-testid="export-toggle-all" className="text-xs text-cyan-400 hover:text-cyan-300">
                  {allOn ? t("Odznacz wszystko") : t("Zaznacz wszystko")}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {MODULE_ORDER.map((k) => {
                const Icon = MODULES[k].icon;
                const on = selected.has(k);
                return (
                  <button key={k} onClick={() => toggle(k)} data-testid={`export-mod-${k}`}
                    className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm text-left ${on ? "border-blue-500 bg-blue-500/10 text-slate-100" : "border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                    <input type="checkbox" readOnly checked={on} className="accent-blue-500 pointer-events-none" />
                    <Icon size={15} className="shrink-0" />
                    <span className="truncate">{t(MODULES[k].label)}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-wide mb-1 block">{t("Paszport sieci — obszar (opcjonalnie)")}</label>
            <input value={area} onChange={(e) => setArea(e.target.value)} data-testid="passport-area" list="passport-area-list"
              placeholder={t("np. miejscowość lub gmina — puste = cała sieć")}
              className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-2 text-sm" />
            <datalist id="passport-area-list">
              {areas.map((a) => <option key={a} value={a} />)}
            </datalist>
          </div>
          <div data-testid="export-shapefile">
            <label className="text-xs text-slate-400 uppercase tracking-wide mb-1 block">{t("Eksport do Shapefile (.zip dla QGIS)")}</label>
            <div className="flex flex-wrap gap-2">
              {[["poles", "Słupy"], ["wells", "Studnie"], ["ducts", "Kanalizacja"], ["microducts", "Mikrokanalizacja"], ["pipelines", "Rurociągi"], ["zones", "Strefy"]].map(([tg, lbl]) => (
                <button key={tg} onClick={() => doShp(tg)} data-testid={`export-shp-${tg}`}
                  className="px-2.5 py-1.5 text-xs rounded border border-emerald-700 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/50 flex items-center gap-1.5">
                  <FileArchive size={13} /> {t(lbl)}
                </button>
              ))}
            </div>
          </div>
          <div data-testid="export-tools" className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <button onClick={doAll} data-testid="export-all-shp"
                className="px-2.5 py-1.5 text-xs rounded border border-cyan-700 bg-cyan-900/30 text-cyan-200 hover:bg-cyan-900/50 flex items-center gap-1.5">
                <FileArchive size={13} /> {t("Eksport całości do jednego ZIP (QGIS)")}
              </button>
              <button onClick={doCov} data-testid="zone-coverage-btn"
                className="px-2.5 py-1.5 text-xs rounded border border-emerald-700 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/50 flex items-center gap-1.5">
                <MapPinned size={13} /> {t("Raport pokrycia stref")}
              </button>
              <button onClick={doCablesXlsx} data-testid="export-cables-xlsx"
                className="px-2.5 py-1.5 text-xs rounded border border-lime-700 bg-lime-900/30 text-lime-200 hover:bg-lime-900/50 flex items-center gap-1.5"
                title={t("Arkusz XLSX kabli: trasa, zapasy, długość optyczna")}>
                <FileBox size={13} /> {t("Kable XLSX (trasa/zapasy/optyczna)")}
              </button>
              <button onClick={doDxf} data-testid="export-dxf"
                className="px-2.5 py-1.5 text-xs rounded border border-orange-700 bg-orange-900/30 text-orange-200 hover:bg-orange-900/50 flex items-center gap-1.5"
                title={t("Eksport całej sieci do DXF (AutoCAD), warstwy wg modułów, układ PUWG 1992")}>
                <FileBox size={13} /> {t("Eksport DXF (AutoCAD)")}
              </button>
            </div>
            {cov && (
              <div data-testid="coverage-report" className="rounded border border-slate-700 bg-slate-900/60 p-2 text-xs">
                <div className="text-slate-400 mb-1">{t("Klienci z lokalizacją")}: <b className="text-slate-200">{cov.total_clients}</b></div>
                {cov.zones.length === 0 && <div className="text-slate-500">{t("Brak stref.")}</div>}
                {cov.zones.map((z, i) => (
                  <div key={i} data-testid={`coverage-row-${i}`} className="flex items-center justify-between py-0.5">
                    <span className="text-slate-300 truncate">{z.name}{z.technology ? ` · ${z.technology}` : ""}</span>
                    <span className="font-mono text-cyan-300 shrink-0">{z.clients} ({z.percent}%)</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {history.length > 0 && (
            <div data-testid="asbuilt-history">
              <div className="text-xs text-slate-400 uppercase tracking-wide mb-1 flex items-center gap-1"><History size={13} /> {t("Historia kompletów powykonawczych")}</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {history.map((h) => (
                  <div key={h.id} className="flex items-center justify-between gap-2 bg-slate-900 border border-slate-800 rounded px-2.5 py-1.5 text-xs" data-testid={`asbuilt-hist-${h.id}`}>
                    <span className="text-slate-300 truncate">
                      {new Date(h.finished_at || h.created_at).toLocaleString("pl-PL")} · {h.total} {t("muf")}{h.route_id ? ` · ${t("trasa")}` : ""}
                    </span>
                    <button onClick={() => downloadHistory(h.id)} disabled={!h.available} data-testid={`asbuilt-hist-dl-${h.id}`}
                      title={h.available ? t("Pobierz ponownie") : t("Plik niedostępny (usunięty)")}
                      className="shrink-0 px-2 py-0.5 rounded border border-emerald-700 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/50 disabled:opacity-40 flex items-center gap-1">
                      <Download size={12} /> {t("Pobierz")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-700 flex justify-between gap-2">
          <button onClick={doPassport} disabled={busy} data-testid="export-passport"
            className="px-3 py-2 text-sm rounded border border-cyan-700 bg-cyan-900/30 text-cyan-200 hover:bg-cyan-900/50 disabled:opacity-50 flex items-center gap-2"
            title={t("Cała sieć podziemna w jednym ZIP: CSV + KML + PDF (audyt UKE)")}>
            <FileBox size={15} /> {t("Paszport sieci (ZIP)")}
          </button>
          <button onClick={doAsBuilt} disabled={busy} data-testid="export-asbuilt"
            className="px-3 py-2 text-sm rounded border border-emerald-700 bg-emerald-900/30 text-emerald-200 hover:bg-emerald-900/50 disabled:opacity-50 flex items-center gap-2"
            title={t("Rozpływ wszystkich muf + metryki łączy w jednym ZIP (odbiór inwestycji)")}>
            {asBuilt ? <Loader2 size={15} className="animate-spin" /> : <FileBox size={15} />}
            {asBuilt ? `${t("Generuję…")} ${asBuilt.done}${asBuilt.total ? "/" + asBuilt.total : ""}` : t("Komplet powykonawczy (ZIP)")}
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded bg-slate-700 hover:bg-slate-600">Anuluj</button>
            <button onClick={doExport} disabled={busy} data-testid="export-run"
              className="px-4 py-2 text-sm rounded bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 flex items-center gap-2">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {busy ? "Eksportuję…" : "Eksportuj"}
            </button>
          </div>
        </div>
      </div>
      {showDxf && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="dxf-options-dialog">
          <div className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <div className="text-sm font-semibold text-slate-100">{t("Eksport DXF — wybierz warstwy i układ")}</div>
              <button onClick={() => setShowDxf(false)} data-testid="dxf-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-slate-400 block mb-1">{t("Układ współrzędnych (EPSG)")}</label>
                <select value={dxfEpsg} onChange={(e) => setDxfEpsg(Number(e.target.value))} data-testid="dxf-epsg"
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100">
                  <option value={2180}>EPSG:2180 – PUWG 1992</option>
                  <option value={2176}>EPSG:2176 – PUWG 2000 strefa 5</option>
                  <option value={2177}>EPSG:2177 – PUWG 2000 strefa 6</option>
                  <option value={2178}>EPSG:2178 – PUWG 2000 strefa 7</option>
                  <option value={2179}>EPSG:2179 – PUWG 2000 strefa 8</option>
                  <option value={4326}>EPSG:4326 – WGS84 (stopnie)</option>
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-slate-400">{t("Moduły (warstwy DXF)")}</label>
                  <div className="flex gap-2 text-[11px]">
                    <button onClick={() => setDxfMods(new Set(DXF_MODS))} className="text-cyan-400 hover:underline">{t("Wszystkie")}</button>
                    <button onClick={() => setDxfMods(new Set())} className="text-slate-400 hover:underline">{t("Żadne")}</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5 max-h-56 overflow-y-auto">
                  {DXF_MODS.map((m) => (
                    <label key={m} data-testid={`dxf-mod-${m}`} className="flex items-center gap-2 text-xs text-slate-200 bg-slate-800/50 rounded px-2 py-1 cursor-pointer">
                      <input type="checkbox" checked={dxfMods.has(m)} onChange={(e) => setDxfMods((s) => { const n = new Set(s); e.target.checked ? n.add(m) : n.delete(m); return n; })} className="accent-orange-500" />
                      {t(MODULES[m]?.label || m)}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-slate-800">
              <button onClick={() => setShowDxf(false)} className="px-3 py-1.5 text-sm text-slate-300 hover:text-white">{t("Anuluj")}</button>
              <button onClick={doDxfDownload} data-testid="dxf-download"
                className="px-4 py-1.5 text-sm bg-orange-600 hover:bg-orange-500 text-white rounded flex items-center gap-1.5">
                <Download size={14} /> {t("Pobierz DXF")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
